/**
 * Placing one catalog or library row in the scene: the core behind the shared
 * `import_asset` tool (this editor's `importAsset` host member) and the Design
 * panel's Library "Place" button, so both go through one placement rule.
 */

import { getAsset } from 'trace-viewer/cms';
import { isDocumentPlacement } from 'trace-viewer/editor';
import { Renderer } from 'trace-viewer/behaviors';
import type { SceneObject } from 'trace-viewer/scene';
import { dispatchCatalogImport } from '../../catalog-import';
import { API_BASE_URL, ensureFreshToken } from '../../catalog-auth';
import { parseCmsMetadata, placementMetadataBehaviors, resolveAssetPlacement } from '../../catalog-api';
import type { ToolContext } from '../agent-helpers';
import { applyTransform, groundAlign, recordCreateForUndo } from '../agent-helpers';
import { catalogIndex } from '../CatalogIndex';
import { isGatewayFile } from '../gateway';
import { importBlobIntoSceneFolder, registerFolderFileName, type FolderImport } from '../../cms/import-into-folder';

type Vec3 = [number, number, number];

/** Structured result from {@link placeCatalogAsset}. */
export interface PlaceCatalogAssetResult {
    ok: boolean;
    message: string;
    sceneObject: SceneObject | null;
}

export interface PlaceCatalogAssetInput {
    assetId?: string;
    url?: string;
    name?: string;
    position?: Vec3;
    rotation?: Vec3;
    scale?: Vec3;
    /** Place it under this object; without one, the top of the scene. */
    parent?: SceneObject | null;
}

/** The file at `url`, put in the scene's folder (or kept in memory until a save, without one). */
async function copyIntoSceneFolder(url: string, ctx: ToolContext): Promise<FolderImport> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`downloading ${url} answered ${res.status}`);
    const fileName = decodeURIComponent(new URL(url).pathname.split('/').pop() || 'file');
    return importBlobIntoSceneFolder(await res.blob(), fileName, ctx.traceScene);
}

/**
 * Place one catalog/library row into the scene as a reference — the shared
 * core behind the `import_asset` tool AND the Design panel's Library "Place"
 * button, so both go through the one placement rule
 * (`resolveAssetPlacement`/§3.4) instead of the panel inventing its own.
 *
 * Registers ONE undo entry: a plain create; nothing else about the scene changes.
 */
export async function placeCatalogAsset(
    input: PlaceCatalogAssetInput,
    ctx: ToolContext,
): Promise<PlaceCatalogAssetResult> {
    const fail = (message: string): PlaceCatalogAssetResult => ({ ok: false, message, sceneObject: null });
    const { assetId, url, name, position, rotation, scale } = input;

    if (!assetId && !url) {
        return fail('Error: Provide asset_id (from search_catalog/browse_folder) or url (e.g. from a hosted get_asset result).');
    }

    // Resolve ids through the local index when possible — it carries titles
    // and behavior presets. Ids from the hosted search may be absent locally
    // (stale index, signed-out panel); a provided url is the fallback.
    let asset: ReturnType<typeof catalogIndex.getById>;
    if (assetId) {
        let indexError = '';
        try {
            await catalogIndex.ensureLoaded();
            asset = catalogIndex.getById(assetId);
        } catch (err) {
            // No local index (signed out of the panel, no team open): the CMS read below, or the url, stands in.
            indexError = ` (${err instanceof Error ? err.message : String(err)})`;
        }
        // The index holds the active team's folders; the shared search covers all of the caller's teams.
        // An id it lacks is read from the CMS, as Studio reads one.
        if (!asset && !url) {
            const token = await ensureFreshToken();
            const record = token ? await getAsset(API_BASE_URL, token, assetId).catch(() => null) : null;
            if (!record || typeof record.url !== 'string' || !record.url) {
                return fail(`Error: Asset "${assetId}" could not be read${indexError}. Use search_catalog to find valid asset IDs, or pass the asset's url directly.`);
            }
            asset = {
                id: assetId, title: typeof record.title === 'string' ? record.title : '', filename: typeof record.originalFilename === 'string' ? record.originalFilename : '',
                url: record.url, source: '', sourceType: 'catalog', behaviors: parseCmsMetadata(record.metadata),
            };
        }
    }

    const viewer = ctx.viewer;
    if (!viewer?.import) {
        return fail('Error: No trace-viewer element available in this context.');
    }

    // A folder row is placed as a **reference** (CONTENT_MODEL_SPEC §3.4):
    // the asset file URL 302s to the asset's current document, so the
    // placement stays live and copies no bytes — the same thing a drag from
    // the catalog panel does. A legacy raw-blob row is placed by its own file
    // (§6.1; `resolveAssetPlacement` tells them apart by the row's url). A
    // url passed explicitly is the caller's business.
    // The row's placement decides both the url and whether the metadata
    // behaviors travel — a document placement carries only what the scene puts
    // on it (SCENE_ROOT_SPEC §2.3). A url passed explicitly has no row behind
    // it, so nothing about it is known but the url itself.
    const placement = asset ? resolveAssetPlacement({ assetId: asset.id, url: asset.url }) : undefined;
    const importUrl = placement ? placement.url : url!;
    const urlBasename = url?.split('/').pop()?.split('?')[0]?.replace(/\.[^/.]+$/, '');
    const importName = name ?? asset?.title ?? asset?.filename?.replace(/\.[^/.]+$/, '') ?? urlBasename ?? 'CatalogAsset';

    try {
        const behaviors = placement
            ? placementMetadataBehaviors(placement, asset?.behaviors, false, ctx.traceScene)
            : asset?.behaviors;
        // A gateway file (a rendered PDF page or area, an attachment) is kept only for a while: copy it into the scene.
        // A catalog row's url is the CMS's own, kept as long as the asset. As for generate_image, an import that fails
        // after the upload leaves the file in the folder, unreferenced.
        const copied = !asset && isGatewayFile(importUrl) ? await copyIntoSceneFolder(importUrl, ctx) : null;
        const { sceneObject } = copied
            ? await viewer.import(copied.file, copied.file.name, importName, behaviors)
            : await viewer.import(importUrl, importName, behaviors);
        if (copied) registerFolderFileName(ctx.traceScene, copied);

        // Ground-align before adding Renderer (matches sandbox UI order).
        groundAlign(sceneObject.transform);

        // Add Renderer behavior for material editing support (if not already
        // present from asset metadata). Never on a document placement: the
        // asset's root owns the geometry one level down, and recolouring it is
        // an edit inside the reference (§2.3 "Editing"), not a placement job.
        if (!isDocumentPlacement(sceneObject) && !sceneObject.getBehavior(Renderer)) {
            await sceneObject.addBehavior(Renderer);
        }

        // A named parent first, keeping the world pose; the pose given is then parent-local.
        if (input.parent) sceneObject.transform.setParent(input.parent.transform);
        applyTransform(sceneObject.transform, position, rotation, scale);

        // The editor owns adoption and its undo entry; no-host callers retain
        // the existing placement undo without recording it twice in the editor.
        // A named parent wins over the editor's open page, keyframe or state context when it adopts the import.
        if (!await dispatchCatalogImport(sceneObject, input.parent ?? undefined)) {
            recordCreateForUndo(sceneObject, sceneObject.getParent());
        }

        // Select the imported object
        ctx.traceScene.select(sceneObject);
        ctx.onSceneChanged?.();

        return {
            ok: true,
            message: `Imported "${sceneObject.name}" from ${asset ? `catalog asset "${asset.title}"` : importUrl}.`,
            sceneObject,
        };
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return fail(`Error importing asset: ${msg}`);
    }
}
